import React from 'react';

import { getPacklaneBrief, getPacklaneBriefVariables } from '__generated__/getPacklaneBrief';
import { PacklaneBriefFields } from '__generated__/PacklaneBriefFields';
import { Currency } from 'common/utils/currency';
import Brief from 'projects/routes/Brief';
import BriefQuery from 'projects/routes/BriefQuery';
import InviteDesigners from 'projects/routes/InviteDesignersPage';
import { BriefStep, StepState } from 'projects/util/step';
import { Route, Switch, useRouteMatch } from 'react-router';
import AboutYouPage from './AboutYouPage';
import DetailsPage from './DetailsPage';
import queries from './Packlane.graphql';
import SummaryPage from './SummaryPage';
import TimeAndBudgetPage from './TimeAndBudgetPage';

interface Props {
    briefId: string;
    step: string;
}

const steps: BriefStep<PacklaneBriefFields>[] = [
    {
        step: 1,
        title: __('Tell us about your project'),
        fields: [
            'language',
            'title',
            'description',
            'referenceFiles',
            'inspirationFiles',
            'inspirationUrls',
        ],
    },
    {
        step: 2,
        title: __('Your time & budget'),
        fields: ['endDate', 'startDate', 'timeframe', 'budget'],
    },
    {
        step: 3,
        title: __('About you'),
        fields: ['industry', 'urls', 'profileApproval', 'other'],
    },
    { step: 4, title: __('Review your brief') },
    {
        step: 5,
        title: __('Invite designers to quote'),
        isAuthRequired: true,
        isDesignerStep: true,
        nextActionLabel: __('Confirm designers'),
    },
];

export function Packlane() {
    const route = useRouteMatch<Props>();

    return (
        <BriefQuery<getPacklaneBrief, getPacklaneBriefVariables>
            query={queries.getPacklaneBrief}
            variables={{ id: route!.params.briefId }}
            fieldsTypename="PacklaneBriefFields"
        >
            {(data, refetch) => {
                const { viewer } = data;
                const brief = { ...data.brief, fields: data.brief.fields as PacklaneBriefFields };

                const step = new StepState({
                    steps,
                    brief: data.brief,
                    currentStep: parseInt(route!.params.step, 10),
                });

                const siteCurrency = data.site.currency as Currency;

                return (
                    <Brief
                        refetch={refetch}
                        schema="packlane"
                        brief={brief}
                        viewer={viewer}
                        stepState={step}
                    >
                        {form => (
                            <Switch>
                                <Route
                                    path={`/briefs/packlane/:briefId/1`}
                                    render={() => <DetailsPage form={form} brief={brief} />}
                                />
                                <Route
                                    path={`/briefs/packlane/:briefId/2`}
                                    render={() => (
                                        <TimeAndBudgetPage
                                            form={form}
                                            brief={brief}
                                            siteCurrency={siteCurrency}
                                        />
                                    )}
                                />
                                <Route
                                    path={`/briefs/packlane/:briefId/3`}
                                    render={() => (
                                        <AboutYouPage
                                            form={form}
                                            brief={brief}
                                            industries={data.industries}
                                        />
                                    )}
                                />
                                <Route
                                    path={`/briefs/packlane/:briefId/4`}
                                    render={() => (
                                        <SummaryPage brief={brief} industries={data.industries} />
                                    )}
                                />
                                <Route
                                    path={`/briefs/packlane/:briefId/5`}
                                    render={() => (
                                        <InviteDesigners
                                            brief={brief}
                                            viewer={data.viewer}
                                            description={__(
                                                "Based on your Packlane brief we've matched you with some of our best product packaging designers"
                                            )}
                                        />
                                    )}
                                />
                            </Switch>
                        )}
                    </Brief>
                );
            }}
        </BriefQuery>
    );
}
